import { type Ref, ref } from 'vue';

export interface TapGuardOptions {
  // A pointer that moves more than this many pixels is a drag, not a tap.
  maxTapDistance?: number;
}

interface TapAnchor {
  pointerId: number;
  x: number;
  y: number;
  scrolled: boolean;
  cancelled: boolean;
}

export interface TapGuard {
  anchor: Ref<TapAnchor | null>;
  onPointerDown: (event: PointerEvent) => void;
  onPointerCancel: (event: PointerEvent) => void;
  onScroll: () => void;
  onClickCapture: (event: MouseEvent) => void;
}

// Keeps a scroll gesture from selecting the tile the finger ended on. The
// browser drops the click after a touch pan, but not after a mouse or pen
// drag, and not when a second pointer joins in. The container records where
// the primary pointer went down, notes any scroll during the gesture, and
// stops the click in its capture phase when the gesture was not a tap.
export function useTapGuard(options: TapGuardOptions = {}): TapGuard {
  const maxTapDistance = options.maxTapDistance ?? 12;
  const anchor = ref<TapAnchor | null>(null);

  function onPointerDown(event: PointerEvent): void {
    if (!event.isPrimary || event.button !== 0) {
      return;
    }
    anchor.value = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      scrolled: false,
      cancelled: false,
    };
  }

  function onPointerCancel(event: PointerEvent): void {
    if (anchor.value?.pointerId === event.pointerId) {
      anchor.value.cancelled = true;
    }
  }

  function onScroll(): void {
    if (anchor.value) {
      anchor.value.scrolled = true;
    }
  }

  function onClickCapture(event: MouseEvent): void {
    const current = anchor.value;
    anchor.value = null;
    if (!current) {
      return;
    }
    const moved =
      Math.abs(event.clientX - current.x) > maxTapDistance ||
      Math.abs(event.clientY - current.y) > maxTapDistance;
    if (moved || current.scrolled || current.cancelled) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  return { anchor, onPointerDown, onPointerCancel, onScroll, onClickCapture };
}
